import $i18n from 'panda-i18n';
import * as React from 'react';
import cx from 'classnames';
import { useValue } from '@/utils/use-value';
import { stopPropagation } from '@/utils/func';
import isNumber from 'lodash/isNumber';
import { CnIcon } from '@/components/cn-icon';
import { CnReadonly } from '@/components/cn-readonly';
import { useFormLayout } from '@/form/cn-form-layout';
import './cn-input.scss';
export const CnInput = React.forwardRef((props, ref) => {
    const { className, size = 'medium', insideForm, type = 'normal', placeholder = $i18n.get({
        id: 'gxms-pc_Enter.CNTM',
        dm: '请输入',
        ns: 'CnInput',
    }), 
    // keyboardType,
    // useOriginalRef,
    disabled, isPassword: isPasswordProp = false, canTogglePasswordShow = true, htmlType, enterKeyHint, state: stateProp, maxLength, showLimitHint = false, readOnly, hasClear, style, autoFocus, align: alignProp, highlightable, addonAfter = null, addonBefore = null, 
    // innerAfter,
    // innerBefore,
    // confirmType,
    // composition,
    composition: compositionProp, onFocus, onClear, onBlur, onChange, onKeyDown, 
    // onConfirm,
    onCompositionStart, onCompositionEnd, } = props;
    const composition = React.useRef(false);
    const inputRef = React.useRef(null);
    const clsPrefix = 'cn-ui-m-input';
    const isPassword = isPasswordProp || htmlType === 'password';
    const curSize = insideForm ? 'small' : size;
    const { wrapperAlign } = useFormLayout();
    const align = alignProp || wrapperAlign;
    const defaultTextType = isPassword ? 'password' : htmlType || 'text';
    // ipuut类型
    const [textType, setTextType] = React.useState(defaultTextType);
    const [isFocused, setIsFocused] = React.useState(false);
    const [value, setValue, isControlled, setPropValue] = useValue(props, '', {
        fitValue: (v) => {
            if (isNumber(maxLength) && maxLength > 0) {
                return `${v}`.length > maxLength ? `${v}`.substr(0, maxLength) : v;
            }
            return v;
        },
    });
    // 右侧是否有icon
    const hasIcon = hasClear || stateProp !== 'normal';
    // 是否展示右侧的删除icon
    const showClearIcon = hasClear && !!value && !readOnly;
    // className样式计算
    const classes = {
        inputContainer: cx(clsPrefix, `${clsPrefix}-size--${curSize}`, `${clsPrefix}--${type}`, {
            [`${clsPrefix}--${curSize}`]: !readOnly,
            [`${clsPrefix}--hasIcon`]: hasIcon,
            [`${clsPrefix}--highlighted`]: highlightable && isFocused,
        }, className),
        input: cx(`${clsPrefix}-wrapper`, `${clsPrefix}--${curSize}`, `${clsPrefix}--${align}`),
        icon: cx(`${clsPrefix}--icon`, `${clsPrefix}--icon-${showClearIcon ? 'clear' : stateProp}`),
    };
    React.useImperativeHandle(ref, () => ({
        clear: () => clearValueHandle(),
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        get nativeElement() {
            return inputRef.current;
        },
    }));
    /** onChange事件 */
    const onChangeHandle = (val, e) => {
        if (isControlled) {
            // 受控模式下同时更新 prop 值
            setPropValue(val);
        }
        else {
            setValue(val);
        }
        // 输入法输入合成中
        if (composition.current) {
            return;
        }
        onChange?.(val, e);
    };
    /** onFocus事件 */
    const onFocusHandle = () => {
        onFocus?.();
        setIsFocused(true);
    };
    /** onBlur事件 */
    const onBlurHandle = (e) => {
        onBlur?.(e);
        setIsFocused(false);
    };
    /** 删除当前内容 */
    const clearValueHandle = () => {
        onChangeHandle('');
        onClear?.();
    };
    /** 切换密码显示隐藏 */
    const togglePasswordHandle = () => {
        if (textType === 'password') {
            setTextType('text');
        }
        else {
            setTextType(defaultTextType);
        }
    };
    /** 只读模式，没有其他 */
    const renderReadOnly = () => {
        return React.createElement(CnReadonly, null, value);
    };
    //修复ios中文直接选择输入内容重复展示
    const handleCompositionStart = (e) => {
        composition.current = true;
        if (onCompositionStart) {
            onCompositionStart(e);
        }
    };
    const handleCompositionEnd = (e) => {
        composition.current = false;
        if (onCompositionEnd) {
            onCompositionEnd(e);
        }
        onChange?.(e.target?.value, e);
    };
    let onCompositionStartBind = onCompositionStart;
    let onCompositionEndBind = onCompositionEnd;
    if (compositionProp) {
        onCompositionStartBind = handleCompositionStart;
        onCompositionEndBind = handleCompositionEnd;
    }
    /** input 输入模式 */
    const renderInput = () => {
        return (React.createElement("input", { ref: inputRef, className: classes.input, style: { ...style }, type: textType, value: value, placeholder: placeholder, autoFocus: autoFocus, maxLength: maxLength, disabled: !!disabled, onInput: (e) => {
                onChangeHandle(e.target.value, e);
            }, onChange: (e) => stopPropagation(e), onBlur: onBlurHandle, onFocus: onFocusHandle, onKeyDown: onKeyDown, enterKeyHint: enterKeyHint, onCompositionStart: onCompositionStartBind, onCompositionEnd: onCompositionEndBind }));
    };
    /** 有限制输入长度时，右侧显示输入字数 */
    const renderLengthTips = () => {
        if (!maxLength || !showLimitHint || readOnly) {
            return null;
        }
        return (React.createElement("div", { className: `${clsPrefix}-length-tips` },
            React.createElement("span", { className: `${clsPrefix}-length-tips-value` }, value.length),
            maxLength ? React.createElement("span", null,
                "/",
                maxLength) : null));
    };
    // 右侧状态控制
    const renderStateIcon = () => {
        const sizeStr = 'small';
        if (showClearIcon) {
            // 删除按钮
            return (React.createElement(CnIcon, { type: "error-r-fill", size: sizeStr, outerClassName: classes.icon, onClick: clearValueHandle }));
        }
        if (canTogglePasswordShow && isPassword) {
            // 密码可切换显示隐藏
            return (React.createElement(CnIcon, { type: textType === 'password' ? 'eye-close-fill' : 'eye-fill', size: sizeStr, outerClassName: classes.icon, onClick: togglePasswordHandle }));
        }
        else {
            switch (stateProp) {
                case 'loading':
                    return (React.createElement(CnIcon, { type: "loading", size: sizeStr, outerClassName: classes.icon }));
                case 'warning':
                    return (React.createElement(CnIcon, { type: "exclamation-t-fill", size: sizeStr, outerClassName: classes.icon }));
                case 'success':
                    return (React.createElement(CnIcon, { type: "complete-r-fill", size: sizeStr, outerClassName: classes.icon }));
                case 'error':
                    return (React.createElement(CnIcon, { type: "error-r-fill", size: sizeStr, outerClassName: classes.icon }));
                default:
                    return null;
            }
        }
    };
    return (React.createElement("div", { className: classes.inputContainer },
        addonBefore,
        readOnly ? renderReadOnly() : renderInput(),
        renderLengthTips(),
        renderStateIcon(),
        addonAfter));
});
CnInput.displayName = 'CnInput';
